type DateFormatInput = string | Date | number | null | undefined;

const DATE_ONLY_PATTERN = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/;

const pad = (value: number): string => value.toString().padStart(2, '0');

const toDateString = (date: Date): string =>
    [
        date.getFullYear().toString(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
    ].join('-');

const toDateTimeString = (date: Date): string =>
    `${toDateString(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

const parseDateOnlyString = (input: string): Date | null => {
    const match = DATE_ONLY_PATTERN.exec(input);
    const groups = match?.groups;

    if (!groups) {
        return null;
    }

    const year = Number(groups.year);
    const month = Number(groups.month);
    const day = Number(groups.day);
    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null;
    }

    return date;
};

const normalizeInput = (
    input: DateFormatInput,
): string | Date | number | null => {
    if (input === null || input === undefined) {
        return null;
    }

    if (typeof input === 'string') {
        const trimmed = input.trim();
        return trimmed === '' ? null : trimmed;
    }

    return input;
};

const parseDate = (input: string | Date | number): Date | null => {
    if (typeof input === 'string') {
        const dateOnly = parseDateOnlyString(input);

        if (dateOnly) {
            return dateOnly;
        }
    }

    const date = new Date(input);

    return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (input: DateFormatInput): string => {
    const normalizedInput = normalizeInput(input);

    if (normalizedInput === null) {
        return '';
    }

    if (
        typeof normalizedInput === 'string' &&
        parseDateOnlyString(normalizedInput)
    ) {
        return normalizedInput;
    }

    const date = parseDate(normalizedInput);

    return date ? toDateString(date) : '';
};

export const formatDateTime = (input: DateFormatInput): string => {
    const normalizedInput = normalizeInput(input);

    if (normalizedInput === null) {
        return '';
    }

    if (
        typeof normalizedInput === 'string' &&
        parseDateOnlyString(normalizedInput)
    ) {
        return `${normalizedInput} 00:00`;
    }

    const date = parseDate(normalizedInput);

    return date ? toDateTimeString(date) : '';
};

// Backward-compatible aliases for older call sites that still use the legacy names.
export const shortDate = formatDate;
export const shortDateTime = formatDateTime;
